import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
} from '@material-ui/core';
import {
  Email,
  Web,
  Search,
  Chat,
  Share,
  Security,
  Warning,
  CheckCircle,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 16,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  headerIcon: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    marginRight: theme.spacing(2),
  },
  vectorItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
  },
  vectorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  vectorIcon: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
  },
  statusChip: {
    fontWeight: 600,
    minWidth: 70,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  summary: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  },
  summaryIcon: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
  },
}));

const LinkVectorAnalysis = ({ getThreatModelData, getTranslation = (text) => text, preferredLanguage = 'EN' }) => {
  const classes = useStyles();

  // Helper functions (defined first to avoid scope issues)
  const getVectorColor = (threatLevel) => {
    switch (threatLevel?.toLowerCase()) {
      case 'safe': return '#4caf50';
      case 'low': return '#8bc34a';
      case 'medium': return '#ff9800';
      case 'high': return '#f44336';
      default: return '#757575';
    }
  };

  const getVectorBackgroundColor = (threatLevel) => {
    switch (threatLevel?.toLowerCase()) {
      case 'safe': return '#e8f5e8';
      case 'low': return '#f1f8e9';
      case 'medium': return '#fff3e0';
      case 'high': return '#ffebee';
      default: return '#f5f5f5';
    }
  };

  const getStatusLabel = (threatLevel) => {
    switch (threatLevel?.toLowerCase()) {
      case 'safe': return getTranslation('Safe', preferredLanguage);
      case 'low': return getTranslation('Low Risk', preferredLanguage);
      case 'medium': return getTranslation('Medium Risk', preferredLanguage);
      case 'high': return getTranslation('High Risk', preferredLanguage);
      default: return getTranslation('Unknown', preferredLanguage);
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  // Process threat model data
  const vectorAnalysis = useMemo(() => {
    // Debug logging
    console.log('LinkVectorAnalysis - getThreatModelData:', getThreatModelData);
    
    if (!getThreatModelData?.thirtyDays?.data || !getThreatModelData?.thirtyDays?.labels) {
      console.log('LinkVectorAnalysis - No threat model data available');
      return {
        vectors: [],
        overallScore: 0,
        criticalCount: 0,
        warningCount: 0,
        secureCount: 0,
        totalThreats: 0,
      };
    }

    // Sum up the 30 days of data for each vector
    const thirtyDaysData = getThreatModelData.thirtyDays.data;
    const labels = getThreatModelData.thirtyDays.labels; // ["sm", "se", "oa", "be", "bc"]
    
    // Calculate totals for each vector (sum across all days)
    const vectorTotals = labels.map((label, index) => {
      const total = thirtyDaysData.reduce((sum, dayData) => {
        // dayData format: ["2025-09-03T00:00:00+00:00", sm, se, oa, be, bc]
        // index + 1 because first element is the date
        return sum + (dayData[index + 1] || 0);
      }, 0);
      return { label, total };
    });

    const vectorConfigs = [
      {
        key: 'sm',
        name: getTranslation('Social Media', preferredLanguage),
        icon: <Share />,
        description: getTranslation('Social platform threats', preferredLanguage),
      },
      {
        key: 'se',
        name: getTranslation('Search Engines', preferredLanguage),
        icon: <Search />,
        description: getTranslation('Search-based threats', preferredLanguage),
      },
      {
        key: 'oa',
        name: getTranslation('Outlook Desktop', preferredLanguage),
        icon: <Email />,
        description: getTranslation('Desktop email threats', preferredLanguage),
      },
      {
        key: 'be',
        name: getTranslation('Browser Email', preferredLanguage),
        icon: <Web />,
        description: getTranslation('Web-based email threats', preferredLanguage),
      },
      {
        key: 'bc',
        name: getTranslation('Browser Chat', preferredLanguage),
        icon: <Chat />,
        description: getTranslation('Web-based chat threats', preferredLanguage),
      },
    ];

    const totalThreats = vectorTotals.reduce((sum, v) => sum + v.total, 0);

    const vectors = vectorConfigs.map(config => {
      const vectorData = vectorTotals.find(v => v.label === config.key);
      const threatCount = vectorData ? vectorData.total : 0;
      const percentage = totalThreats > 0 ? (threatCount / totalThreats) * 100 : 0;
      
      // Determine threat level based on count and percentage
      let threatLevel, score;
      if (threatCount === 0) {
        threatLevel = 'safe';
        score = 90;
      } else if (percentage < 10) {
        threatLevel = 'low';
        score = 75;
      } else if (percentage < 30) {
        threatLevel = 'medium';
        score = 45;
      } else {
        threatLevel = 'high';
        score = 20;
      }
      
      return {
        ...config,
        threatLevel,
        threatCount,
        percentage,
        score,
        color: getVectorColor(threatLevel),
        backgroundColor: getVectorBackgroundColor(threatLevel),
      };
    });

    const totalScore = vectors.reduce((sum, v) => sum + v.score, 0);
    const averageScore = vectors.length > 0 ? totalScore / vectors.length : 0;

    const criticalCount = vectors.filter(v => v.threatLevel === 'high').length;
    const warningCount = vectors.filter(v => v.threatLevel === 'medium' || v.threatLevel === 'low').length;
    const secureCount = vectors.filter(v => v.threatLevel === 'safe').length;

    return {
      vectors,
      overallScore: Math.round(averageScore),
      criticalCount,
      warningCount,
      secureCount,
      totalThreats,
    };
  }, [getThreatModelData]);

  const getOverallStatus = (score) => {
    if (score >= 80) return { label: getTranslation('Excellent', preferredLanguage), color: '#4caf50', icon: <CheckCircle /> };
    if (score >= 60) return { label: getTranslation('Good', preferredLanguage), color: '#ff9800', icon: <Warning /> };
    return { label: getTranslation('Needs Attention', preferredLanguage), color: '#f44336', icon: <Warning /> };
  };

  const overallStatus = getOverallStatus(vectorAnalysis.overallScore);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.header}>
          <Avatar className={classes.headerIcon}>
            <Security />
          </Avatar>
          <Box>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {getTranslation('Link Vector Analysis', preferredLanguage)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {getTranslation('Threat assessment across attack vectors', preferredLanguage)}
            </Typography>
          </Box>
        </Box>

        {/* Vector Analysis */}
        {vectorAnalysis.vectors.map((vector) => (
          <Box key={vector.key} className={classes.vectorItem}>
            <Box className={classes.vectorHeader}>
              <Box display="flex" alignItems="center">
                <Avatar 
                  className={classes.vectorIcon}
                  style={{ 
                    backgroundColor: vector.backgroundColor,
                    color: vector.color,
                    width: 32,
                    height: 32,
                  }}
                >
                  {vector.icon}
                </Avatar>
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {vector.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatNumber(vector.threatCount)} threats • {vector.percentage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              
              <Chip
                size="small"
                label={getStatusLabel(vector.threatLevel)}
                className={classes.statusChip}
                style={{
                  backgroundColor: vector.backgroundColor,
                  color: vector.color,
                }}
              />
            </Box>
            
            <LinearProgress
              variant="determinate"
              value={vector.percentage > 0 ? Math.max(vector.percentage, 5) : 0}
              className={classes.progressBar}
              style={{
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: vector.color,
                },
              }}
            />
          </Box>
        ))}

        {/* Summary */}
        <Box className={classes.summary}>
          <Box display="flex" alignItems="center" marginBottom={1}>
            <Box 
              className={classes.summaryIcon}
              style={{ color: overallStatus.color }}
            >
              {overallStatus.icon}
            </Box>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {getTranslation('Overall Vector Security', preferredLanguage)}: {overallStatus.label}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">
                {getTranslation('Safe', preferredLanguage)}
              </Typography>
              <Typography variant="h6" style={{ color: '#4caf50', fontWeight: 600 }}>
                {vectorAnalysis.secureCount}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">
                {getTranslation('At Risk', preferredLanguage)}
              </Typography>
              <Typography variant="h6" style={{ color: '#ff9800', fontWeight: 600 }}>
                {vectorAnalysis.warningCount}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">
                {getTranslation('High Risk', preferredLanguage)}
              </Typography>
              <Typography variant="h6" style={{ color: '#f44336', fontWeight: 600 }}>
                {vectorAnalysis.criticalCount}
              </Typography>
            </Grid>
          </Grid>
          
          <Box marginTop={1}>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#1976d2' }}>
              {getTranslation('Total Threats', preferredLanguage)} (30 {getTranslation('days', preferredLanguage)}): {formatNumber(vectorAnalysis.totalThreats)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LinkVectorAnalysis;